import { useEffect, type RefObject } from "react";
import type { Stage } from "../types";

export function useActiveStageTracker(
  activeStageId: string,
  stages: Stage[],
  isScrollingRef: RefObject<boolean>,
  setActiveStageIdState: (stageId: string) => void,
) {
  useEffect(() => {
    if (activeStageId === "all") return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const triggerLine = 185; // Below sticky header
      let activeId = "";

      // Check if user is scrolled to the bottom of the page
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50;

      if (isAtBottom && stages.length > 0) {
        activeId = stages[stages.length - 1].id;
      } else {
        // Find which stage section occupies the trigger line
        for (const stage of stages) {
          const el = document.getElementById(`stage-section-${stage.id}`);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= triggerLine && rect.bottom > triggerLine) {
              activeId = stage.id;
              break;
            }
          }
        }

        // Fallback: use closest section to the trigger line
        if (!activeId && stages.length > 0) {
          let minDistance = Number.MAX_VALUE;
          let closestId = stages[0].id;

          for (const stage of stages) {
            const el = document.getElementById(`stage-section-${stage.id}`);
            if (el) {
              const rect = el.getBoundingClientRect();
              const distance = Math.abs(rect.top - triggerLine);
              if (distance < minDistance) {
                minDistance = distance;
                closestId = stage.id;
              }
            }
          }
          activeId = closestId;
        }
      }

      if (activeId && activeId !== activeStageId) {
        setActiveStageIdState(activeId);
        localStorage.setItem("activeStageId_v3", JSON.stringify(activeId));
        localStorage.setItem("activeStageId_timestamp_v3", Date.now().toString());
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeStageId, stages, isScrollingRef, setActiveStageIdState]);
}
